import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const FOLLOW_UP_STEPS = [
  {
    day: 'Day 1',
    label: 'Confirm receipt',
    goal: 'Make sure the estimate actually reached the customer before assuming silence means no.',
    script: 'Hi [Name], I wanted to make sure you received the fence estimate for your project. If anything about the scope, materials or options is unclear, I am happy to walk through it with you.',
  },
  {
    day: 'Day 3',
    label: 'Invite questions',
    goal: 'Open the conversation without immediately discounting your price.',
    script: 'Hi [Name], I wanted to check whether any questions came up while reviewing the fence estimate. I am happy to clarify the scope, materials, timing or options.',
  },
  {
    day: 'Day 7',
    label: 'Check the timeline',
    goal: 'Find out whether the project is delayed, still active or waiting on another decision.',
    script: 'Hi [Name], I am checking in on your fence project. Has your timeline changed, or is there anything you would like to review before deciding on the next step?',
  },
  {
    day: 'Day 14',
    label: 'Offer a scope review',
    goal: 'Give the customer a useful reason to reply without creating unnecessary urgency.',
    script: 'Hi [Name], I wanted to follow up on the fence estimate. If you are still comparing options, I can review the scope with you and make sure the quote reflects what you actually want built.',
  },
  {
    day: 'Day 30',
    label: 'Re-engage or close the loop',
    goal: 'Give the customer an easy way to restart the conversation or tell you the project has changed.',
    script: 'Hi [Name], I wanted to reconnect about the fence estimate we prepared. If the project is still on your list, I can review the scope and next steps with you. If your plans changed, that is helpful to know too.',
  },
];

const FAQ = [
  ['How many times should I follow up on a fence estimate?', 'There is no universal number, but a repeatable sequence is better than a single check-in. A practical starting point is day 1, day 3, day 7, day 14 and a later re-engagement touch. Stop if the customer asks you to, declines the project or gives you a new timeline.'],
  ['What should I avoid saying?', 'Avoid vague messages such as “just following up” with no useful context. Do not invent deadlines, pressure the customer or immediately offer a discount. Give them a clear reason to reply: scope, materials, timing, options or an unresolved question.'],
  ['Should I text, call or email?', 'Use the channel the customer already responds to when possible. Text works well for short check-ins, email is useful when the scope needs context, and a call can be appropriate when the customer has detailed questions. Keep the history together so the next touch does not repeat the last one.'],
  ['When should I stop following up?', 'Stop when the customer declines, asks you not to contact them, chooses another contractor or confirms the project is no longer active. If the project is postponed, record the new timing instead of repeatedly contacting them.'],
];

export default function FenceEstimateFollowUpGuide() {
  const { isAuthenticated } = useAuth();
  const [copied, setCopied] = useState(null);

  const copyScript = async (index) => {
    try {
      await navigator.clipboard.writeText(FOLLOW_UP_STEPS[index].script);
      setCopied(index);
      window.setTimeout(() => setCopied(current => current === index ? null : current), 1800);
    } catch {
      setCopied(null);
    }
  };

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <a href="#guide-start" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-3 focus:text-primary-foreground">
        Skip to follow-up guide
      </a>

      <header className="max-w-5xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span>
            <span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span>
            <span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span>
          </span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link>
          <span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">Fence estimate follow-up guide</span>
        </nav>
      </header>

      <main className="max-w-5xl mx-auto px-4 pb-16 space-y-10">
        <section className="picketops-revenue-surface grid lg:grid-cols-[1.15fr_0.85fr] overflow-hidden rounded-2xl border border-primary/25 bg-card">
          <div className="p-6 sm:p-8 lg:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free field guide · Copy the scripts</p>
            <h1 className="mt-4 max-w-3xl text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">
              How to follow up on a fence estimate without sounding pushy
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
              A sent quote is still an active sales opportunity. Use this five-touch fence estimate follow-up sequence to keep the conversation moving, uncover questions and learn when the customer’s timing changes.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild>
                <a href="#guide-start">See the 5-touch sequence <ApperIcon name="ArrowDown" size={16} /></a>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/contractor-revenue-scorecard">Check my revenue leaks</Link>
              </Button>
            </div>
          </div>
          <div className="border-t lg:border-t-0 lg:border-l border-primary/15 bg-primary/5 p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">The rule to remember</p>
            <p className="mt-4 text-3xl font-heading font-semibold leading-tight">Follow up with a reason to respond.</p>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Receipt, questions, timing, scope and next steps are useful reasons. “Just checking in” is easy to ignore.
            </p>
            <div className="mt-6 grid grid-cols-5 gap-2" aria-label="Follow-up sequence timeline">
              {FOLLOW_UP_STEPS.map(step => (
                <div key={step.day} className="rounded-md border border-border bg-background px-2 py-3 text-center">
                  <p className="text-xs font-semibold text-primary">{step.day}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="guide-start" aria-labelledby="sequence-heading" className="scroll-mt-6 space-y-5">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Fence quote rescue sequence</p>
            <h2 id="sequence-heading" className="mt-2 text-3xl font-heading font-semibold">Five useful touches instead of one vague follow-up.</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Adapt the timing to the customer and the job. If they give you a specific date, use that date instead of continuing the generic sequence.
            </p>
          </div>

          <ol className="space-y-4">
            {FOLLOW_UP_STEPS.map((step, index) => (
              <li key={step.day}>
                <Card className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid md:grid-cols-[180px_minmax(0,1fr)]">
                      <div className="border-b md:border-b-0 md:border-r border-border bg-muted/35 p-5">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary">Touch {index + 1}</p>
                        <p className="mt-2 text-2xl font-heading font-semibold">{step.day}</p>
                        <p className="mt-1 text-sm font-medium">{step.label}</p>
                      </div>
                      <div className="p-5 sm:p-6">
                        <p className="text-sm leading-relaxed text-muted-foreground">{step.goal}</p>
                        <div className="mt-4 rounded-xl border border-border bg-background p-4">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Copy-ready script</p>
                            <Button type="button" variant="ghost" size="sm" onClick={() => copyScript(index)} aria-label={'Copy ' + step.day + ' follow-up script'}>
                              <ApperIcon name={copied === index ? 'Check' : 'Copy'} size={15} />
                              {copied === index ? 'Copied' : 'Copy'}
                            </Button>
                          </div>
                          <p className="mt-3 text-sm leading-relaxed">{step.script}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ol>
        </section>

        <section className="grid lg:grid-cols-2 gap-4" aria-label="Follow-up principles">
          {[
            ['MessageCircleQuestion', 'Ask about the decision, not just the sale', 'Customers may be comparing materials, waiting on another household decision or moving the project date. A useful question tells you what actually changed.'],
            ['BadgeDollarSign', 'Do not lead with a discount', 'Silence does not automatically mean price objection. Clarify the scope and timing before reducing the price or changing the work.'],
            ['CalendarClock', 'Always leave a next date', 'If the customer says “call me next month,” record next month. A real next date is better than repeatedly checking an inbox or relying on memory.'],
            ['History', 'Keep the conversation history together', 'The next message should build on the last one. Centralized notes prevent duplicate questions and make the customer experience feel organized.'],
          ].map(([icon, title, description]) => (
            <Card key={title}>
              <CardContent className="p-5">
                <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <ApperIcon name={icon} size={19} aria-hidden="true" />
                </span>
                <h2 className="mt-4 text-lg font-heading font-semibold">{title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 bg-card">
          <div className="grid lg:grid-cols-[1fr_1fr]">
            <div className="p-6 sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">From guide to workflow</p>
              <h2 className="mt-2 text-3xl font-heading font-semibold">PicketOps turns follow-up into a queue, not a memory test.</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                In the PicketOps workspace, sent estimates can carry a follow-up date, Quote Rescue surfaces the due estimate value, and the next draft can be prepared from the customer record.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild>
                  <Link to={isAuthenticated ? '/follow-up' : '/contractor/signup'}>
                    {isAuthenticated ? 'Open Quote Rescue' : 'Create account'}
                    <ApperIcon name="ArrowRight" size={16} />
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/contractor-revenue-scorecard">Take the free revenue scorecard</Link>
                </Button>
              </div>
              {!isAuthenticated && <p className="mt-3 text-xs text-muted-foreground">Create your company profile, then request operator activation. Support confirms the trial and $49/month founding plan terms. Signup does not start billing.</p>}
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-border bg-muted/30 p-6 sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Simple operating loop</p>
              <ol className="mt-4 space-y-3">
                {[
                  ['1', 'Send the estimate', 'Give the customer a clear scope and next step.'],
                  ['2', 'Set the follow-up date', 'Do not leave the next conversation to memory.'],
                  ['3', 'Work the due queue', 'Start with the estimates that need attention now.'],
                  ['4', 'Record the outcome', 'Accepted, postponed, lost or another follow-up date.'],
                ].map(([number, title, detail]) => (
                  <li key={number} className="flex gap-3 rounded-lg border border-border bg-background p-3">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">{number}</span>
                    <div>
                      <p className="text-sm font-semibold">{title}</p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{detail}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section aria-labelledby="follow-up-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="follow-up-faq-heading" className="text-2xl font-heading font-semibold">Fence estimate follow-up questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">
            {FAQ.map(([question, answer]) => (
              <details key={question} className="py-4">
                <summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <footer className="border-t border-border pt-6 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p>PicketOps · Revenue OS for fence contractors.</p>
            <Link to="/" className="inline-flex min-h-11 items-center gap-2 rounded-md hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Back to PicketOps <ApperIcon name="ArrowRight" size={15} />
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}

export const route = { path: '/fence-estimate-follow-up', layout: 'public' };
